import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getMorningBrief, acknowledgeBrief, BriefData, setTokenProvider } from '../api/client';
import { useAuth } from '../context/AuthContext';

export function MorningBrief() {
  const { getToken } = useAuth();
  const [brief, setBrief] = useState<BriefData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setTokenProvider(getToken);
    loadBrief();
  }, []);

  async function loadBrief() {
    setLoading(true);
    setError('');
    try {
      const data = await getMorningBrief();
      setBrief(data.brief);
    } catch (err: any) {
      setError(err.message || 'Failed to load brief');
    } finally {
      setLoading(false);
    }
  }

  async function handleAcknowledge() {
    try {
      await acknowledgeBrief();
      setAcknowledged(true);
    } catch (err: any) {
      console.error('Acknowledge failed:', err);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-pulse flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
            <div className="h-3 bg-gray-100 rounded w-5/6 mb-2"></div>
            <div className="h-3 bg-gray-100 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Morning Brief</h2>
        <p className="text-red-500 text-sm">{error}</p>
        <button onClick={loadBrief} className="mt-2 text-sm text-[#4AABDE] hover:underline">
          Try again
        </button>
      </div>
    );
  }

  if (!brief) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Weather Strip */}
      <div className="bg-gradient-to-r from-[#1a2332] to-[#2a3a4d] px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3 text-white">
          <span className="text-2xl">{getWeatherEmoji(brief.weather.description)}</span>
          <div>
            <span className="text-lg font-medium">{brief.weather.temp}°F</span>
            <span className="text-gray-300 text-sm ml-2">
              H: {brief.weather.high}° L: {brief.weather.low}°
            </span>
          </div>
          <span className="text-gray-300 text-sm capitalize hidden sm:inline">{brief.weather.description}</span>
        </div>

        {/* Work Summary Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {brief.summary.active > 0 && (
            <span className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full">
              {brief.summary.active} active
            </span>
          )}
          {brief.summary.waiting > 0 && (
            <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-1 rounded-full">
              {brief.summary.waiting} waiting
            </span>
          )}
          {brief.summary.blocked > 0 && (
            <span className="bg-red-500/20 text-red-300 text-xs px-2 py-1 rounded-full">
              {brief.summary.blocked} blocked
            </span>
          )}
          {brief.summary.overdue > 0 && (
            <span className="bg-red-500/20 text-red-300 text-xs px-2 py-1 rounded-full font-medium">
              {brief.summary.overdue} overdue
            </span>
          )}
          {brief.summary.recentHandoffs > 0 && (
            <span className="bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded-full">
              {brief.summary.recentHandoffs} handoff{brief.summary.recentHandoffs > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Brief Content */}
      <div className="p-6">
        <div className="prose prose-sm max-w-none text-gray-700">
          <ReactMarkdown>{brief.text}</ReactMarkdown>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-400">
            Generated {new Date(brief.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            {!brief.weather.available && ' • Weather estimated'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadBrief}
              className="text-xs text-gray-500 hover:text-[#4AABDE] transition-colors"
              title="Refresh brief"
            >
              &#8635; Refresh
            </button>
            {!acknowledged && (
              <button
                onClick={handleAcknowledge}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded transition-colors"
              >
                Got it
              </button>
            )}
            {acknowledged && (
              <span className="text-xs text-green-600">&#10003; Acknowledged</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getWeatherEmoji(description: string): string {
  const lower = description.toLowerCase();
  if (lower.includes('rain') || lower.includes('drizzle')) return '🌧️';
  if (lower.includes('thunder') || lower.includes('storm')) return '⛈️';
  if (lower.includes('cloud') || lower.includes('overcast')) return '☁️';
  if (lower.includes('partly') || lower.includes('scattered')) return '⛅';
  if (lower.includes('snow')) return '🌨️';
  if (lower.includes('fog') || lower.includes('mist') || lower.includes('haze')) return '🌫️';
  if (lower.includes('wind')) return '💨';
  return '☀️';  // Default: sunny (it's Phoenix)
}
